import { useEffect } from "react"
import { createWithEqualityFn as create } from "zustand/traditional"
import { useTypeformUserFields } from "./use-typeform-user-fields"
import { GENERAL_FEEDBACK_FORM_ID } from "./feedback-constants"

const SCRIPT_SRC = "https://embed.typeform.com/next/embed.js"
const POPUP_CSS_SRC = "https://embed.typeform.com/next/css/popup.css"

const POPUP_WIDTH = "min(720px, calc(100vw - 32px))"
const POPUP_HEIGHT = "min(560px, calc(100vh - 32px))"

let typeformLoading = null

// Loaded on first use rather than on mount: the triggers live in the nav, so loading
// eagerly would put Typeform's script on every page whether or not anyone opens it.
function loadTypeform() {
  if (!document.querySelector(`link[href="${POPUP_CSS_SRC}"]`)) {
    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = POPUP_CSS_SRC
    document.head.append(link)
  }

  if (window.tf?.createPopup) return Promise.resolve(window.tf)
  if (typeformLoading) return typeformLoading

  // TypeformEmbed may have added the script already, via useScript, and its load or error event
  // may have fired already, so check where it got to instead of waiting on it.
  let script = document.querySelector(`script[src="${SCRIPT_SRC}"]`)
  const status = script?.getAttribute("data-status")

  // Loaded without window.tf.createPopup (checked above): waiting won't change that.
  if (status === "ready") {
    return Promise.reject(new Error("Typeform loaded without createPopup"))
  }

  // useScript leaves a script that failed in the DOM, so retry with a fresh one.
  if (status === "error") {
    script.remove()
    script = null
  }

  if (!script) {
    script = document.createElement("script")
    script.src = SCRIPT_SRC
    script.async = true
    // useScript reads this to tell later embeds (e.g. TypeformSidetab) the script is ready.
    script.setAttribute("data-status", "loading")
    document.body.append(script)
  }

  typeformLoading = new Promise((resolve, reject) => {
    script.addEventListener("load", () => {
      script.setAttribute("data-status", "ready")
      typeformLoading = null
      resolve(window.tf)
    })
    script.addEventListener("error", () => {
      script.setAttribute("data-status", "error")
      typeformLoading = null
      script.remove()
      reject(new Error("Typeform failed to load"))
    })
  })

  return typeformLoading
}

// Lets the page on screen choose which form "Give feedback" in the account menu opens. The nav
// is mounted once in the layout, outside the page, so it can't read the page's own context.
export const usePageFeedbackFormStore = create(() => ({ formId: null }))

// Pass null to leave the menu on the general form. Cleared on unmount, so an Inertia visit to a
// page that doesn't call this falls back to the general form.
export function usePageFeedbackForm(formId) {
  useEffect(() => {
    if (!formId) return
    usePageFeedbackFormStore.setState({ formId })

    return () => {
      if (usePageFeedbackFormStore.getState().formId === formId) {
        usePageFeedbackFormStore.setState({ formId: null })
      }
    }
  }, [formId])
}

const formUrl = (formId, hiddenFields) => {
  const hash = new URLSearchParams(hiddenFields).toString()
  return `https://form.typeform.com/to/${formId}#${hash}`
}

// Opens a Typeform as a popup from our own trigger, instead of Typeform's floating launcher.
// Falls back to the form in a new tab when the script is blocked (ad blockers commonly do).
export function useFeedbackPopup({
  formId = GENERAL_FEEDBACK_FORM_ID,
  hiddenFields = {}
} = {}) {
  const userFields = useTypeformUserFields()

  return async () => {
    const hidden = {
      ...userFields,
      ...hiddenFields,
      page_path:
        window.location.pathname + window.location.search + window.location.hash
    }

    try {
      const tf = await loadTypeform()
      // Typeform only applies a fixed size when both are set; otherwise it fills the page.
      tf.createPopup(formId, {
        hidden,
        width: POPUP_WIDTH,
        height: POPUP_HEIGHT
      }).open()
    } catch {
      window.open(formUrl(formId, hidden), "_blank", "noopener")
    }
  }
}
